// Increment 2 + Native chat Spec 01 — consume the social-chat data topic, and
// (for native chat) send/moderate through the server-verified edge functions.
//
// Origin gate (two accepted sources on the `social` topic):
//   1. RELAY  — packets from the SOCIAL_RELAY_IDENTITY participant (external
//      chat/superchats via the bridge). Sound because livekit-token refuses to
//      mint that identity to a normal user.
//   2. SERVER — participant-LESS packets (native chat + chat-delete, published by
//      live-chat-send / live-chat-moderate via RoomServiceClient.sendData). A
//      browser can never publish participant-less data — the SFU always stamps
//      the publisher — so `participant === undefined` ⟹ server-origin, unforgeable.
// Everything else is rejected. SHAPE is then validated by decodeSocialEvent.
//
// Native chat is server-verified: send()/moderate() POST to the edge functions;
// clients never write the `social` topic themselves.

import {
  SOCIAL_CHAT_TOPIC,
  SOCIAL_RELAY_IDENTITY,
  decodeSocialEvent,
  type SocialChatMessage,
  type SuperchatMessage,
} from '@archetelos/lib/live';
import { RoomEvent } from 'livekit-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Participant, Room } from 'livekit-client';

import { useAuth } from '@/lib/auth/useAuth';
import { supabase } from '@/lib/supabase';

const DEFAULT_BUFFER_SIZE = 200;
const MAX_SUPERCHATS = 100;
const BACKFILL_LIMIT = 50;
const COOLDOWN_MS = 3_000; // gentle client mirror of the server rate limit

/** Optimistic-send client state layered on a native chat message (not on the wire). */
export interface FeedChat extends SocialChatMessage {
  pending?: 'sending' | 'failed';
  failReason?: 'error' | 'rate_limited' | 'muted';
  /** Host view only (keepDeleted): a chat-delete arrived — render a tombstone. */
  deleted?: boolean;
}
export type FeedEntry = FeedChat | SuperchatMessage;

export interface ModerateArgs {
  messageId?: string;
  userId?: string;
}

export interface UseSocialChatResult {
  messages: FeedEntry[];
  /** Count of leading backfilled (pre-join) messages — the LIVE divider index. */
  backfillCount: number;
  superchats: SuperchatMessage[];
  totalReceived: number;
  rejectedCount: number;
  /** True once a send returned 403 muted — compose disabled, feed still readable. */
  muted: boolean;
  /** Epoch ms until which sending is on cooldown (429 or the gentle mirror), else null. */
  cooldownUntil: number | null;
  send: (text: string) => Promise<void>;
  retry: (nonce: string) => Promise<void>;
  dismiss: (nonce: string) => void;
  moderate: (action: 'delete' | 'mute' | 'unmute', args: ModerateArgs) => Promise<void>;
}

export function useSocialChat(
  room: Room | null,
  opts: { bufferSize?: number; keepDeleted?: boolean } = {},
): UseSocialChatResult {
  const bufferSize = opts.bufferSize ?? DEFAULT_BUFFER_SIZE;
  const keepDeleted = opts.keepDeleted ?? false;
  const { session, profile } = useAuth();

  const [messages, setMessages] = useState<FeedEntry[]>([]);
  const [backfillCount, setBackfillCount] = useState(0);
  const [totalReceived, setTotalReceived] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [muted, setMuted] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);

  const seenRef = useRef<Set<string>>(new Set());
  const roomName = room?.name ?? null;

  const rememberId = useCallback(
    (id: string) => {
      const seen = seenRef.current;
      seen.add(id);
      if (seen.size > bufferSize * 4) {
        const oldest = seen.values().next().value;
        if (oldest !== undefined) seen.delete(oldest);
      }
    },
    [bufferSize],
  );

  const appendLive = useCallback(
    (msg: FeedEntry) => {
      setMessages((prev) =>
        prev.length >= bufferSize + BACKFILL_LIMIT
          ? [...prev.slice(prev.length - (bufferSize + BACKFILL_LIMIT) + 1), msg]
          : [...prev, msg],
      );
    },
    [bufferSize],
  );

  // --- Receive (relay OR server-origin) → decode → dedupe/nonce-merge ----
  useEffect(() => {
    if (!room) return;
    const onData = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: unknown,
      topic?: string,
    ): void => {
      if (topic !== SOCIAL_CHAT_TOPIC) return;

      const relay = participant?.identity === SOCIAL_RELAY_IDENTITY;
      const serverOrigin = participant === undefined; // participant-less ⟹ server
      if (!relay && !serverOrigin) {
        setRejectedCount((n) => n + 1);
        return;
      }

      const msg = decodeSocialEvent(payload);
      if (!msg) {
        setRejectedCount((n) => n + 1);
        return;
      }

      // Control: remove a deleted message everywhere (server-origin only). The
      // host view keeps it as a tombstone (keepDeleted); viewers drop it.
      if (msg.kind === 'chat-delete') {
        if (!serverOrigin) return;
        setMessages((prev) =>
          keepDeleted
            ? prev.map((m) =>
                m.id === msg.id && m.kind === 'social-chat' ? { ...m, deleted: true } : m,
              )
            : prev.filter((m) => m.id !== msg.id),
        );
        return;
      }

      // A native echo carrying a nonce replaces the optimistic local entry.
      if (msg.kind === 'social-chat' && msg.nonce) {
        setMessages((prev) =>
          prev.filter((m) => !(m.kind === 'social-chat' && m.nonce === msg.nonce)),
        );
      }

      if (seenRef.current.has(msg.id)) return; // dedupe by id
      rememberId(msg.id);
      setTotalReceived((n) => n + 1);
      appendLive(msg);
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [room, rememberId, appendLive, keepDeleted]);

  // --- Backfill: last 50 native messages for the room, prepended on mount ---
  useEffect(() => {
    if (!roomName || !session) return;
    let cancelled = false;
    void (async () => {
      const { data: roomRow } = await supabase
        .from('rooms')
        .select('id')
        .eq('livekit_room_name', roomName)
        .maybeSingle();
      if (cancelled || !roomRow) return;
      const { data: rows } = await supabase
        .from('live_chat_messages')
        .select('id, role, text, created_at, sender_id')
        .eq('room_id', roomRow.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(BACKFILL_LIMIT);
      if (cancelled || !rows || rows.length === 0) {
        setBackfillCount(0);
        return;
      }

      // Resolve sender display names (profiles are public-readable). One query.
      const senderIds = [...new Set(rows.map((r) => r.sender_id))];
      const { data: profs } = await supabase
        .from('profiles')
        .select('id, display_name, username')
        .in('id', senderIds);
      if (cancelled) return;
      const nameById = new Map(
        (profs ?? []).map((p) => [p.id, p.display_name ?? p.username ?? 'Member'] as const),
      );

      const backfill: FeedChat[] = rows
        .slice()
        .reverse() // ascending (oldest first) ahead of live
        .filter((r) => !seenRef.current.has(r.id))
        .map((r) => ({
          v: 1,
          kind: 'social-chat',
          id: r.id,
          platform: 'archetelos',
          author: { name: nameById.get(r.sender_id) ?? 'Member' },
          text: r.text,
          ts: Date.parse(r.created_at),
          senderId: r.sender_id,
          ...(r.role === 'host' || r.role === 'cohost' ? { role: r.role } : {}),
        }));
      backfill.forEach((m) => rememberId(m.id));
      setBackfillCount(backfill.length);
      setMessages((prev) => [...backfill, ...prev]);
    })();
    return () => {
      cancelled = true;
    };
  }, [roomName, session, rememberId]);

  // --- Send (optimistic → server-verified) -------------------------------
  const setPending = useCallback((nonce: string, patch: Partial<FeedChat> | null) => {
    setMessages((prev) => {
      if (patch === null)
        return prev.filter((m) => !(m.kind === 'social-chat' && m.nonce === nonce));
      return prev.map((m) =>
        m.kind === 'social-chat' && m.nonce === nonce ? { ...m, ...patch } : m,
      );
    });
  }, []);

  const doSend = useCallback(
    async (text: string, nonce: string): Promise<void> => {
      if (!roomName) return;
      const { data, error } = await supabase.functions.invoke('live-chat-send', {
        body: { roomId: roomName, text, nonce },
      });
      // supabase.functions.invoke surfaces non-2xx as an error with a context.
      if (error) {
        let status = 0;
        let payload: { error?: string; retryAfterMs?: number } = {};
        try {
          const res = (error as { context?: Response }).context;
          if (res) {
            status = res.status;
            payload = await res.clone().json();
          }
        } catch {
          /* opaque error */
        }
        if (status === 403 && payload.error === 'muted') {
          setMuted(true);
          setPending(nonce, { pending: 'failed', failReason: 'muted' });
          return;
        }
        if (status === 429) {
          setCooldownUntil(Date.now() + (payload.retryAfterMs ?? COOLDOWN_MS));
          setPending(nonce, { pending: 'failed', failReason: 'rate_limited' });
          return;
        }
        setPending(nonce, { pending: 'failed', failReason: 'error' });
        return;
      }
      // Success. The server echo (same nonce) replaces the optimistic entry;
      // set a gentle cooldown so the user doesn't immediately hit the 3s limit.
      void data;
      setCooldownUntil(Date.now() + COOLDOWN_MS);
    },
    [roomName, setPending],
  );

  const send = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (!trimmed || !roomName || !session) return;
      const nonce = crypto.randomUUID();
      const optimistic: FeedChat = {
        v: 1,
        kind: 'social-chat',
        id: `local:${nonce}`,
        platform: 'archetelos',
        author: { name: profile?.display_name ?? profile?.username ?? 'You' },
        text: trimmed.slice(0, 280),
        ts: Date.now(),
        nonce,
        senderId: session.user.id,
        pending: 'sending',
      };
      appendLive(optimistic);
      await doSend(optimistic.text, nonce);
    },
    [roomName, session, profile, appendLive, doSend],
  );

  const retry = useCallback(
    async (nonce: string): Promise<void> => {
      let text = '';
      setMessages((prev) =>
        prev.map((m) => {
          if (m.kind === 'social-chat' && m.nonce === nonce) {
            text = m.text;
            return { ...m, pending: 'sending', failReason: undefined };
          }
          return m;
        }),
      );
      if (text) await doSend(text, nonce);
    },
    [doSend],
  );

  const dismiss = useCallback((nonce: string) => setPending(nonce, null), [setPending]);

  const moderate = useCallback(
    async (action: 'delete' | 'mute' | 'unmute', args: ModerateArgs): Promise<void> => {
      if (!roomName) return;
      const { error } = await supabase.functions.invoke('live-chat-moderate', {
        body: { action, roomId: roomName, ...args },
      });
      if (error) throw error;
    },
    [roomName],
  );

  const superchats = useMemo(
    () =>
      messages
        .filter((m): m is SuperchatMessage => m.kind === 'superchat')
        .sort((a, b) => b.amount.value - a.amount.value || a.ts - b.ts)
        .slice(0, MAX_SUPERCHATS),
    [messages],
  );

  return {
    messages,
    backfillCount,
    superchats,
    totalReceived,
    rejectedCount,
    muted,
    cooldownUntil,
    send,
    retry,
    dismiss,
    moderate,
  };
}
